import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import { SpinnerDefault } from "../../components";
import { convertStringDateToObjDate } from "../../utils";
import { PaymentModal, ServiceSection, PaymentSummaryCard } from "./components";
import { useAsync, useSharedState, useUrlParams } from "../../hooks";
import { PaymentProvider } from "./PaymentProcessor/PaymentProcessor";
import BookingService from "../../services/booking/BookingService";
import { routes } from "../../app/routes";
import { LocationService } from "../../services";
import { PaymentGatewayType } from "../../models";
import { useInvoiceCalculations } from "./hooks";
import { clearBookingStorage, trackBookingPurchase, trackCheckoutStep, trackDraftEvent } from "../../utils";
import { LocationCard, CustomerCard } from "../booking/components";

export const InvoicePage: React.FC = () => {
  const navigate = useNavigate();
  const locationCode = useUrlParams("locationCode"); 
  
  const locationService = new LocationService();
  const bookingService = new BookingService();

  const activeBookingIdStr = localStorage.getItem("activeBookingId");
  const activeBookingId = activeBookingIdStr ? parseInt(activeBookingIdStr) : null;

  const anyProviderFlags = localStorage.getItem("activeBookingAnyProviderFlags");
  const anyProviderFlagsArray: boolean[] = anyProviderFlags ? JSON.parse(anyProviderFlags) : [];

  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [totalDuration, setTotalDuration] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [timeLeftMs, setTimeLeftMs] = useState<number | null>(null);
  const [showExpiredModal, setShowExpiredModal] = useState(false);

  useEffect(() => {
    if (showExpiredModal) trackDraftEvent("booking_expired", activeBookingId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showExpiredModal]);

  // Countdown timer
  useEffect(() => {
    if (!expiresAt) return;

    const tick = () => {
      const now = new Date();
      const diff = expiresAt.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeLeft("Expired");
        setTimeLeftMs(0);
        setShowExpiredModal(true);
        clearInterval(interval);  
        return;
      }

      setTimeLeftMs(diff);
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${minutes}:${seconds.toString().padStart(2, "0")}`);
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  const { value: bookingInvoice, loading: loadingBooking } = useAsync(async () => {
    if (activeBookingId) {
      try {
        const draftStatus = await bookingService.getDraftStatus(activeBookingId);
        if (!draftStatus) return;
        if (draftStatus.status !== "active") {
          clearBookingStorage();
          return null;
        }

        if (draftStatus.expiresAtUtc) {
          const expiry = new Date(draftStatus.expiresAtUtc);
          setExpiresAt(expiry);
          setTotalDuration(expiry.getTime() - Date.now());
        } else {
          setExpiresAt(null);
          setTotalDuration(null);
        }

        const result = await bookingService.getBookingWithInvoiceAsync(activeBookingId);
        
        if (result?.booking) {
          if (result.booking.date) {
            result.booking.date = convertStringDateToObjDate(result.booking.date);
          }
          if (result.booking.bookingEvents) {
            result.booking.bookingEvents = result.booking.bookingEvents.map((event: any) => ({
              ...event,
              date: convertStringDateToObjDate(event.date),
            }));
          }
        }
        return result;
      } catch {
        clearBookingStorage();
        return null;
      }
    }
    return null;
  }, [activeBookingId]);

  const booking = bookingInvoice?.booking;
  const invoice = bookingInvoice?.invoice;

  const anyProviderMap: Record<number, boolean> = {};
  booking?.bookingEvents?.forEach((event, index) => {
    anyProviderMap[event.id] = anyProviderFlagsArray[index] ?? false;
  });

  const {
    chargeEvents,
    holdEvents,
    noChargeEvents,
    totalChargesSubtotal,
    totalHoldsSubtotal,
    chargesTax,
    holdsTax,
    totalCharges,
    totalHolds,
    hasCharges,
    hasHolds,
  } = useInvoiceCalculations({ booking, invoice });

  const { value: paymentProvider } = useAsync(
    () => booking?.location ? locationService.getPublicPaymentOptionsAsync(booking.location.id) : Promise.resolve(null),
    [booking?.location]
  );
  const paymentGatewayType = paymentProvider?.name as PaymentGatewayType;

  const showPaymentModal = useSharedState(false);

  const isDraftExpired = () => {
    if (!expiresAt) return true;
    return new Date() >= expiresAt;
  };

  const handlePayment = () => {
    if (isDraftExpired()) {
      clearBookingStorage();
      setShowExpiredModal(true);
      return;
    }
    trackCheckoutStep("add_payment_info", booking!, invoice!);
    showPaymentModal.setter(true);
  };

  const handleConfirm = async () => {
    if (isDraftExpired()) {
      clearBookingStorage();
      setShowExpiredModal(true);
      return;
    }

    await bookingService.markBookingAsCompletedAsync(booking!.id);
    trackBookingPurchase(booking!, invoice!);
    clearBookingStorage();
    navigate(
      routes.book.confirmation.replace(":locationCode", locationCode),
      { 
        state: {
          booking: booking, 
          invoice: invoice 
        }
      }
    );
  };

  const handleExpiredRedirect = async () => {
    setShowExpiredModal(false);

    if (activeBookingId && invoice?.id) {
      try {
        await bookingService.discardDraftBookingAsync(activeBookingId, invoice.id);
      } catch {
      }
    }
    clearBookingStorage();
    navigate(routes.book.index.replace(":locationCode", locationCode));
  };

  // No active booking
  if (!activeBookingId) {
    return (
      <div className="container mt-5 text-center" style={{ maxWidth: '600px' }}>
        <div className="p-5">
          <i className="fa fa-calendar fa-3x mb-3 text-muted"></i>
          <h3>No Active Booking</h3>
          <p className="text-muted mb-4">
            You don't have a booking in progress. Please start a new booking.
          </p>
          <Button 
            variant="primary"
            onClick={() => navigate(routes.book.index.replace(":locationCode", locationCode))}
          >
            Start New Booking
          </Button>
        </div>
      </div>
    );
  }

  if (loadingBooking) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <SpinnerDefault />
      </div>
    );
  }

  // Booking expired or not found
  if (!booking || !invoice) {
    return (
      <div className="container mt-5 text-center" style={{ maxWidth: '600px' }}>
        <div className="p-5">
          <i className="fa fa-clock fa-3x mb-3 text-muted"></i>
          <h3>Booking Expired</h3>
          <p className="text-muted mb-4">
            Your booking session has expired. Please start a new booking.
          </p>
          <Button 
            variant="primary"
            onClick={() => {
              clearBookingStorage();
              navigate(routes.book.index.replace(":locationCode", locationCode));
            }}
          >
            Start New Booking
          </Button>
        </div>
      </div>
    );
  }

  const handleBack = async () => {
    try {
      if (activeBookingId) {
        await bookingService.discardDraftBookingAsync(activeBookingId, invoice.id);
      }
      clearBookingStorage();
      navigate(routes.book.index.replace(":locationCode", locationCode), {
        state: {
          restoreBooking: booking,
          anyProviderFlags: anyProviderFlagsArray,
        }
      })
    } catch (error) {
      console.error("Error preparing edit mode:", error)
    }
  }

  return (
    <>
      <div className="container mt-0 mt-md-5" style={{ maxWidth: '900px', marginBottom: '80px' }}>
        {/* Header */}
        <div className="mb-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start mb-4">
            <div className="mb-3 mb-md-0">
              <h2 className="mb-1 fw-bold">Invoice</h2>
              <p className="text-muted mb-0">#{invoice.number}</p>
            </div>
            <div className="text-start text-md-end">
              <p className="mb-1"><strong>Date</strong></p>
              <p className="text-muted mb-0">{new Date().toLocaleDateString("en-US", {month: "long", day: "numeric", year: "numeric"})}</p>
              {timeLeft && timeLeft !== "Expired" && (
                <p className={`mb-0 small fw-semibold mt-1 ${
                  totalDuration && timeLeftMs != null && timeLeftMs < totalDuration * 0.2
                    ? "text-danger"
                    : "text-muted"
                }`}>
                  <i className="fa fa-clock me-1"></i>
                  Booking expires in {timeLeft}
                </p>
              )}
            </div>
          </div>

          <div className="row g-3">
            {/* Customer Card */}
            <div className="col-24 col-md-12">
              <CustomerCard
                firstName={booking.customer?.firstName}
                lastName={booking.customer?.lastName}
                email={booking.customer?.email}
                phone={booking.customer?.phone}
              />
            </div>

            {/* Location Card */}
            <div className="col-24 col-md-12">
              <LocationCard
                name={booking.location?.name}
                streetAddress={booking.location?.address?.streetAddress}
                city={booking.location?.address?.city?.trim()}
                province={booking.location?.address?.region?.abbreviation}
                postalCode={booking.location?.address?.postalCode}
              />
            </div>
          </div>
        </div>

        {/* Service Sections with Payment Summaries */}
        <ServiceSection
          title="Services - Due Now"
          icon="fa-credit-card"
          iconColor="text-primary-pastel-bold"
          events={chargeEvents}
          bookingDate={booking.date}
          sectionType="charge"
          tax={chargesTax}
          anyProviderFlags={anyProviderMap}
        />

        <PaymentSummaryCard
          type="charge"
          totalAmount={totalCharges}
          subtotal={totalChargesSubtotal}
          tax={chargesTax}
          appointmentCount={booking.bookingEvents?.length || 1}
        />

        <ServiceSection
          title="On Hold - Balance Due at Appointment"
          icon="fa-lock"
          iconColor="text-secondary-pastel-bold "
          events={holdEvents}
          bookingDate={booking.date}
          sectionType="hold"
          tax={holdsTax}
          anyProviderFlags={anyProviderMap}
        />

        <PaymentSummaryCard
          type="hold"
          totalAmount={totalHolds}
          subtotal={totalHoldsSubtotal}
          tax={holdsTax}
          appointmentCount={booking.bookingEvents?.length || 1}
        />

        <ServiceSection
          title="Pay Balance at Appointment"
          icon="fa-calendar-check"
          iconColor="#2f4157"
          events={noChargeEvents}
          bookingDate={booking.date}
          sectionType="none"
          anyProviderFlags={anyProviderMap}
        />

        {!hasCharges && !hasHolds && (
          <PaymentSummaryCard
            type="none"
            totalAmount={0}
            appointmentCount={booking.bookingEvents?.length || 1}
          />
        )}
      </div>

      {!showPaymentModal.value && (
        <div 
          className="position-fixed bottom-0 start-0 w-100 py-2 px-3 border-top shadow-lg"
          style={{ 
            zIndex: 1000,
            backgroundColor: '#f8f9fa'
          }}
        >
          <div className="container" style={{ maxWidth: '800px' }}>
            <div className="d-flex gap-2">
              <Button 
                variant="secondary" 
                onClick={handleBack} 
                className="w-50"
                style={{ padding: '0.4rem 0.8rem' }}
              >
                <i className="fa fa-arrow-left me-1"></i>
                Back
              </Button>
              <Button 
                variant="primary" 
                onClick={invoice.total === 0 ? handleConfirm : handlePayment}
                className="w-50"
                style={{ padding: '0.4rem 0.8rem' }}
              >
                {invoice.total === 0 ? "Confirm Booking" : `Pay`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {invoice.total > 0 && (
        <PaymentProvider invoice={invoice} paymentGateway={paymentGatewayType}>
          <PaymentModal
            booking={booking}
            invoice={invoice}
            showState={showPaymentModal}
            locationCode={locationCode}
            paymentGatewayType={paymentGatewayType}
          />
        </PaymentProvider>
      )}

      {/* Expired Modal */}
      <Modal show={showExpiredModal} centered backdrop="static" keyboard={false}>
        <Modal.Header>
          <Modal.Title>Booking Expired</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <i className="fa fa-clock fa-3x mb-3 text-muted"></i>
            <p className="mb-0">
              Your booking session has expired. Please start a new booking.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleExpiredRedirect}>
            Start New Booking
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default InvoicePage;